import { useCallback, useEffect, useRef, useState } from "react"
import { useWebSockets } from "../../Websockets"

// A person the current user should see as actively typing in a thread.
export type TTypingUser = {
	id: number
	name: string
}

// The whisper payload other clients broadcast while typing.
type TTypingWhisper = TTypingUser & {
	// A "typing" whisper starts/extends the indicator; a "stop" clears it fast
	// (fired on send/blur) so the bubble doesn't linger for its full TTL.
	stopped?: boolean
}

// How long a single "typing" whisper keeps someone in the indicator before it
// is pruned. Kept a little above the sender's throttle window so a steady
// typist never flickers out between whispers.
const TYPING_TTL_MS = 4000
// Leading-edge throttle: the first keystroke whispers immediately, then at most
// one whisper per window while typing continues.
const NOTIFY_THROTTLE_MS = 2000
// How often the prune timer sweeps expired entries.
const PRUNE_INTERVAL_MS = 1000

/**
 * Realtime "X is typing…" for a WhatsApp thread, built entirely on Laravel Echo
 * client events (whispers) over the existing private `wa.thread.{id}` channel —
 * no backend, no DB. `notifyTyping()` whispers the current user's identity on a
 * leading throttle; incoming whispers from *other* users are collected into
 * `typingUsers` and expire on a TTL so a client that goes silent (or closes)
 * drops out without needing an explicit stop.
 */
export function useThreadTyping(
	threadId: number | null | undefined,
	currentUser: TTypingUser | null | undefined,
	// When set, typing is *also* whispered on the tenant channel `wa.tenant.{id}`
	// (emit-only — the inbox list owns that channel's subscribe/leave) so the
	// thread list can show a per-row "typing…" without any per-thread channels.
	tenantId?: number | null
) {
	const client = useWebSockets()
	const channelName = threadId ? `wa.thread.${threadId}` : null
	const tenantChannelName = tenantId ? `wa.tenant.${tenantId}` : null

	// userId -> { name, expiresAt }. A ref holds the source of truth so the
	// whisper listener and prune timer mutate it without re-subscribing, and a
	// derived state array drives the render.
	const entriesRef = useRef<Map<number, { name: string; expiresAt: number }>>(
		new Map()
	)
	const [typingUsers, setTypingUsers] = useState<Array<TTypingUser>>([])

	// Keep the latest current user in a ref so the subscribe effect stays stable
	// (it only depends on client + channel) while still filtering out self.
	const currentUserRef = useRef(currentUser)
	useEffect(() => {
		currentUserRef.current = currentUser
	}, [currentUser])

	const publish = useCallback(() => {
		const next: Array<TTypingUser> = []
		entriesRef.current.forEach((entry, id) => {
			next.push({ id, name: entry.name })
		})
		setTypingUsers(next)
	}, [])

	useEffect(() => {
		if (!client || !channelName) {
			return
		}
		const channel = client.private(channelName)
		// The Map instance is stable across renders (never reassigned), so this
		// snapshot is safe to use in the cleanup below.
		const entries = entriesRef.current

		const onTyping = (payload: TTypingWhisper) => {
			const self = currentUserRef.current
			if (self && payload.id === self.id) {
				return
			}
			if (payload.stopped) {
				if (entries.delete(payload.id)) {
					publish()
				}
				return
			}
			entries.set(payload.id, {
				name: payload.name,
				expiresAt: nowMs() + TYPING_TTL_MS,
			})
			publish()
		}

		channel.listenForWhisper("typing", onTyping)

		// Prune expired typists so a client that stopped whispering (idle, tab
		// closed, network drop) fades out on its own.
		const timer = window.setInterval(() => {
			const now = nowMs()
			let changed = false
			entries.forEach((entry, id) => {
				if (entry.expiresAt <= now) {
					entries.delete(id)
					changed = true
				}
			})
			if (changed) {
				publish()
			}
		}, PRUNE_INTERVAL_MS)

		return () => {
			channel.stopListeningForWhisper("typing", onTyping)
			window.clearInterval(timer)
			entries.clear()
			setTypingUsers([])
		}
		// The channel lifecycle (subscribe/leave) is owned by the thread's message
		// hook on the same channel name; here we only attach the whisper listener.
	}, [client, channelName, publish])

	// Timestamp of the last "typing" whisper we sent, for the leading throttle.
	const lastNotifyRef = useRef(0)

	const notifyTyping = useCallback(() => {
		if (!client || !channelName) {
			return
		}
		const self = currentUserRef.current
		if (!self) {
			return
		}
		const now = nowMs()
		if (now - lastNotifyRef.current < NOTIFY_THROTTLE_MS) {
			return
		}
		lastNotifyRef.current = now
		client
			.private(channelName)
			.whisper("typing", { id: self.id, name: self.name })
		// Fan the same signal to the inbox list. Emit-only: the list already holds
		// this channel, so we never subscribe or leave it here.
		if (tenantChannelName && threadId) {
			client.private(tenantChannelName).whisper("typing", {
				id: self.id,
				name: self.name,
				threadId,
			})
		}
	}, [client, channelName, tenantChannelName, threadId])

	const stopTyping = useCallback(() => {
		if (!client || !channelName) {
			return
		}
		const self = currentUserRef.current
		if (!self) {
			return
		}
		// Allow the next keystroke to whisper immediately rather than waiting out
		// the throttle window left over from before the stop.
		lastNotifyRef.current = 0
		client
			.private(channelName)
			.whisper("typing", { id: self.id, name: self.name, stopped: true })
		if (tenantChannelName && threadId) {
			client.private(tenantChannelName).whisper("typing", {
				id: self.id,
				name: self.name,
				threadId,
				stopped: true,
			})
		}
	}, [client, channelName, tenantChannelName, threadId])

	return { typingUsers, notifyTyping, stopTyping }
}

// The tenant-channel whisper payload the inbox list consumes: a thread-scoped
// variant of the per-thread whisper.
type TInboxTypingWhisper = TTypingWhisper & {
	threadId: number
}

/**
 * Inbox-list counterpart to `useThreadTyping`: listens for `typing` whispers on
 * the tenant channel `wa.tenant.{id}` — the same channel the list already
 * subscribes to for activity — and returns which users are currently typing in
 * each thread. Listen-only: the list's activity subscription owns the channel
 * lifecycle, so this only attaches/detaches the whisper handler. Self is
 * filtered out, and entries expire on the same TTL as the thread view.
 */
export function useInboxTyping(
	tenantId: number | null | undefined,
	currentUser: TTypingUser | null | undefined
) {
	const client = useWebSockets()
	const channelName = tenantId ? `wa.tenant.${tenantId}` : null

	// `${threadId}:${userId}` -> entry, so the same person typing in two threads
	// (or two people in one) are tracked independently.
	const entriesRef = useRef<
		Map<
			string,
			{ threadId: number; id: number; name: string; expiresAt: number }
		>
	>(new Map())
	const [typingByThread, setTypingByThread] = useState<
		Record<number, Array<TTypingUser>>
	>({})

	const currentUserRef = useRef(currentUser)
	useEffect(() => {
		currentUserRef.current = currentUser
	}, [currentUser])

	const publish = useCallback(() => {
		const byThread: Record<number, Array<TTypingUser>> = {}
		entriesRef.current.forEach((entry) => {
			;(byThread[entry.threadId] ??= []).push({
				id: entry.id,
				name: entry.name,
			})
		})
		setTypingByThread(byThread)
	}, [])

	useEffect(() => {
		if (!client || !channelName) {
			return
		}
		const channel = client.private(channelName)
		const entries = entriesRef.current
		const keyFor = (threadId: number, id: number) => `${threadId}:${id}`

		const onTyping = (payload: TInboxTypingWhisper) => {
			if (!payload.threadId) {
				return
			}
			const self = currentUserRef.current
			if (self && payload.id === self.id) {
				return
			}
			const key = keyFor(payload.threadId, payload.id)
			if (payload.stopped) {
				if (entries.delete(key)) {
					publish()
				}
				return
			}
			entries.set(key, {
				threadId: payload.threadId,
				id: payload.id,
				name: payload.name,
				expiresAt: nowMs() + TYPING_TTL_MS,
			})
			publish()
		}

		channel.listenForWhisper("typing", onTyping)

		const timer = window.setInterval(() => {
			const now = nowMs()
			let changed = false
			entries.forEach((entry, key) => {
				if (entry.expiresAt <= now) {
					entries.delete(key)
					changed = true
				}
			})
			if (changed) {
				publish()
			}
		}, PRUNE_INTERVAL_MS)

		return () => {
			channel.stopListeningForWhisper("typing", onTyping)
			window.clearInterval(timer)
			entries.clear()
			setTypingByThread({})
		}
	}, [client, channelName, publish])

	return typingByThread
}

// `Date.now()` wrapper kept in one place so the TTL/throttle math reads clean.
function nowMs(): number {
	return Date.now()
}
